import {
  Autocomplete,
  AutocompleteRenderInputParams,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import {
  PartnerAgencyOption,
  PartnerCustomFilterSummary,
  PartnerReportTypeOption,
} from "@rubi/api-generated";

import {
  DateRangePickerCustom,
  FilterChip,
  FilterChipGroup,
  RenderFilterChips,
  SearchBarFilters,
} from "@/components/alerts/Search/SearchFilters";
import { reportColourTypes } from "@/types/typesReportV2";

import {
  partnerStatusOptions,
  partnerStatusQuery,
  partnerStatusTypes,
} from "./partnerStatusLabels";

/** Partner-list filter state. Multi-selects are stored pipe-delimited so they map straight onto the
 * backend query params; dates are `yyyy-mm-dd`. */
export interface PartnerFilters {
  status: string | null;
  leAgency: string | null;
  reportType: string | null;
  submittedStart: string | null;
  submittedEnd: string | null;
  trackingNumber: string | null;
  incidentNumber: string | null;
  hasSupplemental: boolean | null;
  custom: Record<string, string[]>;
  /** Null when the grid's sort is cleared; the backend then falls back to last-updated. */
  sortBy: string | null;
  sortOrder: "asc" | "desc";
}

export const emptyPartnerFilters: PartnerFilters = {
  status: null,
  leAgency: null,
  reportType: null,
  submittedStart: null,
  submittedEnd: null,
  trackingNumber: null,
  incidentNumber: null,
  hasSupplemental: null,
  custom: {},
  sortBy: "time_submitted",
  sortOrder: "desc",
};

export interface PartnerFilterOptions {
  agencies: PartnerAgencyOption[];
  reportTypes: PartnerReportTypeOption[];
  customFilters: PartnerCustomFilterSummary[];
}

const customValues = (filters: PartnerFilters) => filters.custom ?? {};

const withCustomValues = (
  filters: PartnerFilters,
  id: string,
  values: string[],
): PartnerFilters => {
  const rest = Object.fromEntries(
    Object.entries(customValues(filters)).filter(([key]) => key !== id),
  );
  return {
    ...filters,
    custom: values.length > 0 ? { ...rest, [id]: values } : rest,
  };
};

const customOptionLabel = (filter: PartnerCustomFilterSummary, value: string) =>
  filter.options?.find((o) => o.value === value)?.label ?? value;

export const customSelectionsStillValid = (
  selections: Record<string, string[]>,
  configured: PartnerCustomFilterSummary[],
) =>
  Object.entries(selections).every(([id, values]) => {
    const filter = configured.find((f) => f.id === id);
    if (!filter) return false;
    if (!filter.multiple && values.length > 1) return false;
    const { options } = filter;
    return !options || values.every((v) => options.some((o) => o.value === v));
  });

const CustomFilterInput = ({
  filter,
  values,
  onChange,
}: {
  filter: PartnerCustomFilterSummary;
  values: string[];
  onChange: (values: string[]) => void;
}) => {
  if (!filter.options) {
    return (
      <TextField
        label={filter.label}
        variant="outlined"
        fullWidth
        value={values[0] ?? ""}
        onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
      />
    );
  }
  const optionValues = filter.options.map((o) => o.value);
  const getOptionLabel = (value: string) => customOptionLabel(filter, value);
  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField {...params} label={filter.label} variant="outlined" />
  );
  return filter.multiple ? (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={optionValues}
      getOptionLabel={getOptionLabel}
      value={values}
      onChange={(_, value) => onChange(value)}
      renderInput={renderInput}
    />
  ) : (
    <Autocomplete
      options={optionValues}
      getOptionLabel={getOptionLabel}
      value={values[0] ?? null}
      onChange={(_, value) => onChange(value ? [value] : [])}
      renderInput={renderInput}
    />
  );
};

const splitPipe = (value: string | null): string[] =>
  value ? value.split("|").filter(Boolean) : [];

const joinPipe = (values: string[]): string | null =>
  values.length > 0 ? values.join("|") : null;

export const partnerFiltersToQuery = (filters: PartnerFilters) => ({
  status: partnerStatusQuery(filters.status),
  le_agency: filters.leAgency,
  report_type: filters.reportType,
  submitted_start: filters.submittedStart
    ? new Date(`${filters.submittedStart}T00:00:00`).toISOString()
    : null,
  submitted_end: filters.submittedEnd
    ? new Date(`${filters.submittedEnd}T23:59:59.999`).toISOString()
    : null,
  tracking_number: filters.trackingNumber,
  incident_number: filters.incidentNumber,
  has_supplemental: filters.hasSupplemental,
  custom_filters:
    Object.keys(customValues(filters)).length > 0
      ? JSON.stringify(customValues(filters))
      : null,
});

export const PartnerReportFilters = ({
  isOpen,
  anchorEl,
  onClose,
  filters,
  setFilters,
  options,
}: {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  filters: PartnerFilters;
  setFilters: (filters: PartnerFilters) => void;
  options: PartnerFilterOptions;
}) => {
  const agencyTitle = (id: string) =>
    options.agencies.find((a) => a.id === id)?.title ?? id;
  const reportTypeName = (surveyClass: string) =>
    options.reportTypes.find((t) => t.survey_class === surveyClass)?.name ??
    surveyClass;

  const gridElements = [
    <Grid size={{ xs: 6 }} key="status">
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={partnerStatusOptions}
        getOptionLabel={(status) => partnerStatusTypes[status] ?? status}
        value={splitPipe(filters.status)}
        onChange={(_, value) =>
          setFilters({ ...filters, status: joinPipe(value) })
        }
        renderInput={(params) => (
          <TextField {...params} label="Status" variant="outlined" />
        )}
      />
    </Grid>,
    <Grid size={{ xs: 6 }} key="le-agency">
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options.agencies.map((a) => a.id)}
        getOptionLabel={agencyTitle}
        value={splitPipe(filters.leAgency)}
        onChange={(_, value) =>
          setFilters({ ...filters, leAgency: joinPipe(value) })
        }
        renderInput={(params) => (
          <TextField {...params} label="LE Agency" variant="outlined" />
        )}
      />
    </Grid>,
    <Grid size={{ xs: 6 }} key="report-type">
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options.reportTypes.map((t) => t.survey_class)}
        getOptionLabel={reportTypeName}
        value={splitPipe(filters.reportType)}
        onChange={(_, value) =>
          setFilters({ ...filters, reportType: joinPipe(value) })
        }
        renderInput={(params) => (
          <TextField {...params} label="Report Type" variant="outlined" />
        )}
      />
    </Grid>,
    ...options.customFilters.map((filter) => (
      <Grid size={{ xs: 6 }} key={`custom-${filter.id}`}>
        <CustomFilterInput
          filter={filter}
          values={customValues(filters)[filter.id] ?? []}
          onChange={(values) =>
            setFilters(withCustomValues(filters, filter.id, values))
          }
        />
      </Grid>
    )),
    <Grid size={{ xs: 6 }} key="has-supplemental">
      <FormControl fullWidth>
        <InputLabel id="partner-has-supp-label">Supplemental</InputLabel>
        <Select
          labelId="partner-has-supp-label"
          label="Supplemental"
          value={
            filters.hasSupplemental === null
              ? ""
              : String(filters.hasSupplemental)
          }
          onChange={(e) => {
            const value = e.target.value as string;
            setFilters({
              ...filters,
              hasSupplemental: value === "" ? null : value === "true",
            });
          }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="true">Attached</MenuItem>
          <MenuItem value="false">Not attached</MenuItem>
        </Select>
      </FormControl>
    </Grid>,
    <Grid size={{ xs: 6 }} key="tracking-number">
      <TextField
        label="Rubicon #"
        variant="outlined"
        fullWidth
        value={filters.trackingNumber ?? ""}
        onChange={(e) =>
          setFilters({ ...filters, trackingNumber: e.target.value || null })
        }
      />
    </Grid>,
    <Grid size={{ xs: 6 }} key="incident-number">
      <TextField
        label="Incident #"
        variant="outlined"
        fullWidth
        value={filters.incidentNumber ?? ""}
        onChange={(e) =>
          setFilters({ ...filters, incidentNumber: e.target.value || null })
        }
      />
    </Grid>,
    <Grid size={{ xs: 12 }} key="submitted-range">
      <DateRangePickerCustom
        labelFrom="Submitted From"
        labelTo="Submitted To"
        startDate={filters.submittedStart ?? ""}
        endDate={filters.submittedEnd ?? ""}
        onStartChange={(value) =>
          setFilters({ ...filters, submittedStart: value || null })
        }
        onEndChange={(value) =>
          setFilters({ ...filters, submittedEnd: value || null })
        }
      />
    </Grid>,
  ];

  return (
    <SearchBarFilters
      isOpen={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      gridElements={gridElements}
    />
  );
};

export const PartnerReportFilterChips = ({
  filters,
  setFilters,
  options,
}: {
  filters: PartnerFilters;
  setFilters: (filters: PartnerFilters) => void;
  options: PartnerFilterOptions;
}) => {
  const removeFromPipe =
    (key: "status" | "leAgency" | "reportType") => (value: string) =>
      setFilters({
        ...filters,
        [key]: joinPipe(splitPipe(filters[key]).filter((v) => v !== value)),
      });

  const chipGroup = (
    key: string,
    values: string[],
    title: string,
    label: (value: string) => string,
    onDelete: (value: string) => void,
    onClear: () => void,
    color?: (value: string) => "default" | "info" | "success" | "warning",
  ) =>
    values.length > 0 ? (
      <FilterChipGroup
        key={key}
        title={title}
        values={values}
        getLabel={label}
        getColor={color}
        onRemove={onDelete}
        onClear={onClear}
      />
    ) : null;

  const multiChip = (
    key: "status" | "leAgency" | "reportType",
    title: string,
    label: (value: string) => string,
    color?: (value: string) => "default" | "info" | "success" | "warning",
  ) =>
    chipGroup(
      key,
      splitPipe(filters[key]),
      title,
      label,
      removeFromPipe(key),
      () => setFilters({ ...filters, [key]: null }),
      color,
    );

  const singleChip = (
    key: keyof PartnerFilters,
    title: string,
    display: string,
  ) => (
    <FilterChip
      key={key}
      title={title}
      value={display}
      displayFullLabel
      onRemove={() => setFilters({ ...filters, [key]: null })}
    />
  );

  const chips: (JSX.Element | null)[] = [
    multiChip(
      "status",
      "Status",
      (v) => partnerStatusTypes[v] ?? v,
      (v) => reportColourTypes[v] ?? "default",
    ),
    multiChip(
      "leAgency",
      "Agency",
      (id) => options.agencies.find((a) => a.id === id)?.title ?? id,
    ),
    multiChip(
      "reportType",
      "Report Type",
      (surveyClass) =>
        options.reportTypes.find((t) => t.survey_class === surveyClass)?.name ??
        surveyClass,
    ),
    ...options.customFilters.map((filter) => {
      const values = customValues(filters)[filter.id] ?? [];
      return chipGroup(
        `custom-${filter.id}`,
        values,
        filter.label,
        (value) => customOptionLabel(filter, value),
        (value) =>
          setFilters(
            withCustomValues(
              filters,
              filter.id,
              values.filter((v) => v !== value),
            ),
          ),
        () => setFilters(withCustomValues(filters, filter.id, [])),
      );
    }),
    filters.hasSupplemental !== null
      ? singleChip(
          "hasSupplemental",
          "Supplemental",
          filters.hasSupplemental ? "Attached" : "Not attached",
        )
      : null,
    filters.trackingNumber
      ? singleChip("trackingNumber", "Rubicon #", filters.trackingNumber)
      : null,
    filters.incidentNumber
      ? singleChip("incidentNumber", "Incident #", filters.incidentNumber)
      : null,
    filters.submittedStart
      ? singleChip("submittedStart", "Submitted From", filters.submittedStart)
      : null,
    filters.submittedEnd
      ? singleChip("submittedEnd", "Submitted To", filters.submittedEnd)
      : null,
  ];

  return (
    <RenderFilterChips
      filterChipsActive={chips}
      onClearAll={() =>
        setFilters({
          ...emptyPartnerFilters,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
        })
      }
    />
  );
};
